<template>
	<div id="quarterly-table">
		<div class="item-box mb-32">
			<CommonTab
				type="inner"
				:value.sync="financeQuarterActValue"
				:option="financeQuarterTabList"
				@change="changefinanceQuarterTab"
			/>
			<div>{{ unit }}</div>
		</div>
		<el-table
			v-loading="quarterlyPerformance_financialStatement.loading"
			:data="tableDataList[financeActValue]"
			class="table-box"
			border
			:key="timeStamp"
		>
			<el-table-column
				prop="title"
				:label="proxy.$t('enterprise.indicators')"
				fixed="left"
				:min-width="isEN ? 200 : 140"
				align="left"
			>
			</el-table-column>
			<el-table-column v-for="(item, index) in dateList" :key="index" :label="item" min-width="170" align="right">
				<template slot-scope="scope">
					{{ thousandSeparator(scope.row[item]) }}
				</template>
			</el-table-column>
		</el-table>
	</div>
</template>

<script>
export default {};
</script>

<script setup>
import { ref, reactive, computed, getCurrentInstance, watch } from 'vue';
import CommonTab from '@/components/CommonTab.vue';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import i18n, { EN } from '@/locales';
import { convertNumberToCNUnit } from '@/utils/api.js';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';

const isEN = computed(() => i18n.locale == EN);
const { proxy } = getCurrentInstance();

const timeStamp = ref('');
const thousandSeparator = value => {
	if (typeof value == 'string') {
		return value;
	} else if (!isNaN(parseFloat(value))) {
		if (isEN.value) {
			const { value: En_value, unit: En_unit } = convertNumberToCNUnit(Math.abs(value));
			return value > 0 ? `${En_value}${En_unit}` : `-${En_value}${En_unit}`;
		}

		let num = parseFloat(value);
		let val, unit;
		if ((num >= 10000 && num < 100000000) || (num < -10000 && num > -100000000)) {
			unit = proxy.$t('enterprise.ten-thousand');
			val = (parseInt(num / 100) / 100).toFixed(2);
		} else if (num >= 100000000 || num < -100000000) {
			unit = proxy.$t('enterprise.one-hundred-million');
			val = (parseInt(num / 1000000) / 100).toFixed(2);
		} else if (num == 0) {
			val = '-';
			unit = '';
		} else {
			val = num.toFixed(2);
			unit = '';
		}
		return val.toLocaleString() + unit;
	} else {
		return '';
	}
};

const props = defineProps({
	financial: {
		type: String,
		default: 'financial'
	}
});

// 从父组件接收 financeActValue
let financeActValue = computed(() => props.financial);

const { getFullCode } = useStockVO();

const residueFinancialItems = computed(() =>
	getFullCode.value.match(/hk/g)
		? [proxy.$t('enterprise.current-debt-ration'), proxy.$t('enterprise.operating-income-per-share')]
		: [proxy.$t('enterprise.CapSurFdPS'), proxy.$t('enterprise.ups')]
);

const financialTitle = computed(() => [
	'ROE',
	'ROA',
	proxy.$t('enterprise.bvps'),
	proxy.$t('enterprise.cfps'),
	proxy.$t('enterprise.gross-margin'),
	proxy.$t('enterprise.eps'),
	...residueFinancialItems.value
]);

const profitTitle = computed(() => [
	proxy.$t('enterprise.eps'),
	proxy.$t('enterprise.diluted-eps'),
	proxy.$t('enterprise.total-operating-revenue'),
	proxy.$t('enterprise.net-profit'),
	proxy.$t('enterprise.operating-profit'),
	proxy.$t('enterprise.net-profit-of-parent-company-owners')
]);

const residueBalanceItems = computed(() =>
	getFullCode.value.match(/hk/g)
		? [proxy.$t('enterprise.non-current-liabilitise')]
		: [
				proxy.$t('enterprise.long-term-loans'),
				proxy.$t('enterprise.non-current-liabilitise'),
				proxy.$t('enterprise.paid-in-capital'),
				proxy.$t('enterprise.retained-profit')
		  ]
);

const balanceSheetTitle = computed(() => [
	proxy.$t('enterprise.money-capital'),
	proxy.$t('enterprise.current-assets'),
	proxy.$t('enterprise.non-current-assets'),
	proxy.$t('enterprise.current-liabilitise'),
	...residueBalanceItems.value,
	proxy.$t('enterprise.owners-equity')
]);

const cashFlowTitle = computed(() => [
	proxy.$t('enterprise.operating-cash-flow'),
	proxy.$t('enterprise.investing-cash-flow'),
	proxy.$t('enterprise.finaning-cash-flow')
]);

const handleTabchange = data => {
	if (!data) data = financeActValue.value;
	switch (data) {
		case 'financial':
			tableTitle.value = financialTitle.value;
			getFinancialStatement();
			break;
		case 'profit':
			tableTitle.value = profitTitle.value;
			getProfitStatement();
			break;
		case 'balanceSheet':
			tableTitle.value = balanceSheetTitle.value;
			getBalanceSheet();
			break;
		case 'cashFlow':
			tableTitle.value = cashFlowTitle.value;
			getCashFlowStatement();
	}
};

let tableTitle = ref(financialTitle.value);

let financeQuarterActValue = ref('');

let financeQuarterTabList = computed(() => [
	{
		label: proxy.$t('enterprise.all'),
		value: ''
	},
	{
		label: proxy.$t('enterprise.finance-data.first-quarter'),
		value: 'Q1'
	},
	{
		label: proxy.$t('enterprise.finance-data.interim'),
		value: 'S1'
	},
	{
		label: proxy.$t('enterprise.finance-data.third-quarter'),
		value: 'Q3'
	},
	{
		label: proxy.$t('enterprise.finance-data.annual'),
		value: 'A'
	}
]);
const changefinanceQuarterTab = ({ index, data }) => {
	financeQuarterActValue.value = data.value;
	handleTabchange(financeActValue.value);
};

const dateList = ref([]);
const tableDataList = reactive({
	financial: [],
	cashFlow: [],
	profit: [],
	balanceSheet: []
});
const residueKeys = getFullCode.value.match(/hk/g) ? { assetLiabRatio: [], revPs: [] } : { reserPs: [], rePs: [] };
let financialKeys = { roe: [], roa: [], nAssetPs: [], nCInCashPs: [], grossMargin: [], eps: [], ...residueKeys };
let profitKeys = { basicEps: [], dilutedEps: [], tRevenue: [], nIncome: [], operateProfit: [], nIncomeAttrP: [] };
const residueBalanceKeys = getFullCode.value.match(/hk/g)
	? { tNcl: [] }
	: { ltBorr: [], tNcl: [], paidInCapital: [], retainedEarnings: [] };
let balanceSheetKeys = {
	monetaryFunds: [],
	tCa: [],
	tNca: [],
	tCl: [],
	...residueBalanceKeys,
	tEquityAttrP: []
};
let cashFlowKeys = { nCfOperateA: [], nCfFrInvestA: [], nCfFrFinanA: [] };
const unit = ref('');

const handleData = (objKeys, data) => {
	if (!data.length) {
		dateList.value = [];
		return (tableDataList[financeActValue.value] = []);
	}
	timeStamp.value = new Date() + '';
	Object.keys(objKeys).forEach(key => (objKeys[key] = [])); //进来先清空之前存储的数据
	dateList.value = data?.map(item => item.publishDate); //获取日期列表
	let target = tableTitle.value?.map(item => ({ title: item })); //

	Object.keys(objKeys)?.forEach(key => {
		data?.forEach((item, index) => {
			objKeys[key].push(item[key]);
			if (item.currencyCd && index == 0) unit.value = item['currencyCd'];
		});
	});

	let cacheArr = [];
	Object.values(objKeys)?.forEach(arr => {
		let obj = {};
		dateList.value?.forEach((key, index) => {
			obj[key] = arr[index];
		});
		cacheArr.push(obj);
	});

	tableDataList[financeActValue.value] = target?.map((item, index) => {
		return {
			...item,
			...cacheArr[index]
		};
	});
};

const {
	quarterlyPerformance_financialStatement,
	quarterlyPerformance_profitStatement,
	quarterlyPerformance_balanceSheet,
	quarterlyPerformance_cashFlowStatement
} = useModuleData();
//获取主要财务指标
const getFinancialStatement = async () => {
	await quarterlyPerformance_financialStatement.refresh({
		reportType: financeQuarterActValue.value,
		publishDate: ''
	});

	quarterlyPerformance_financialStatement.data?.data.forEach(item => {
		Object.keys(item).forEach(key => {
			if (['roe', 'roa', 'grossMargin', 'assetLiabRatio'].includes(key)) {
				item[key] = item[key].toFixed(2) + '%';
			}
		});
	});
	handleData(financialKeys, quarterlyPerformance_financialStatement.data?.data);
};
//利润表
const getProfitStatement = async () => {
	await quarterlyPerformance_profitStatement.refresh({
		reportType: financeQuarterActValue.value,
		publishDate: ''
	});
	handleData(profitKeys, quarterlyPerformance_profitStatement.data?.data);
};
//资产负债表
const getBalanceSheet = async () => {
	await quarterlyPerformance_balanceSheet.refresh({
		reportType: financeQuarterActValue.value,
		publishDate: ''
	});
	handleData(balanceSheetKeys, quarterlyPerformance_balanceSheet.data?.data);
};
//现金流量表
const getCashFlowStatement = async () => {
	await quarterlyPerformance_cashFlowStatement.refresh({
		reportType: financeQuarterActValue.value,
		publishDate: ''
	});
	handleData(cashFlowKeys, quarterlyPerformance_cashFlowStatement.data?.data);
};

// 监听语言类型更改
watch(
	() => proxy.$i18n.locale,
	newVal => {
		handleTabchange(financeActValue.value);
	},
	{ immediate: true }
);

// 暴露方法给父组件调用
defineExpose({
	getFinancialStatement,
	handleTabchange
});
</script>

<style lang="less" scoped>
.table-box {
	width: 100%;
}

.item-box {
	display: flex;
	justify-content: space-between;
}
</style>
